import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, FormControlLabel, IconButton, InputAdornment, MenuItem, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { CloneDetail, LogLine } from '@api/api';
import { endpoints, type LogQuery } from '../../api/endpoints';
import { openStream } from '../../api/sse';
import { ErrorState, EmptyState } from '../../components/common';
import { VirtualLog } from '../../components/VirtualLog';
import { fmtTime } from '../../lib/format';
import { MONO } from '../../theme';

export const SERVICES = ['app', 'sockets', 'worker', 'cache', 'telemetry', 'mongo', 'redis', 'redis-cache', 'rabbitmq', 'mosquitto', 'iot', 'simulator'];
const SINCE = ['5m', '15m', '1h', '6h', '24h'];
const LEVELS = ['error', 'warn', 'info'];
const MAX_LINES = 5000;

const levelColor: Record<string, string> = { error: 'error.main', warn: 'warning.main', info: 'text.primary', debug: 'text.disabled' };

/** Pretty-print a JSON log line; null when it isn't JSON. */
export function prettyJson(raw: string): string | null {
  const s = raw.trim();
  if (!s.startsWith('{')) return null;
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return null;
  }
}

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

function Row({ l, open, onToggle }: { l: LogLine; open: boolean; onToggle: () => void }) {
  const pretty = open ? prettyJson(l.raw) : null;
  return (
    <Box
      onClick={onToggle}
      sx={{ px: 1.5, py: 0.1, cursor: 'pointer', borderLeft: 3, borderColor: l.level === 'error' ? 'error.main' : l.level === 'warn' ? 'warning.main' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
        <Box component="span" sx={{ color: 'text.disabled', flexShrink: 0 }}>
          {l.ts ? fmtTime(l.ts) : '        '}
        </Box>
        <Box component="span" sx={{ color: 'primary.main', flexShrink: 0, width: 72, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {l.service}
        </Box>
        <Box component="span" sx={{ color: levelColor[l.level ?? 'info'], flexShrink: 0, width: 40, textTransform: 'uppercase', fontSize: 11 }}>
          {l.level ?? ''}
        </Box>
        <Box component="span" sx={{ color: levelColor[l.level ?? 'info'], whiteSpace: open ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, wordBreak: 'break-all' }}>
          {l.msg}
        </Box>
      </Box>
      {open && (
        <Box component="pre" sx={{ m: 0, ml: { xs: 0, sm: 17 }, my: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: MONO, fontSize: 12 }}>
          {pretty ?? l.raw}
        </Box>
      )}
    </Box>
  );
}

export function LogsTab({ clone: c }: { clone: CloneDetail }) {
  const [params, setParams] = useSearchParams();
  const [service, setService] = useState(params.get('service') ?? '');
  const [since, setSince] = useState(params.get('since') ?? '15m');
  const [level, setLevel] = useState(params.get('level') ?? '');
  const [text, setText] = useState(params.get('q') ?? '');
  const [follow, setFollow] = useState(false);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [liveLines, setLiveLines] = useState<LogLine[]>([]);
  const [streamState, setStreamState] = useState<'idle' | 'open' | 'error'>('idle');
  const q = useDebounced(text, 350);

  const query: LogQuery = useMemo(() => ({ service: service || undefined, since, level: level || undefined, q: q || undefined, tail: 1000 }), [service, since, level, q]);

  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries({ service, since: since === '15m' ? '' : since, level, q }).forEach(([k, v]) => v && p.set(k, v));
    setParams(p, { replace: true });
  }, [service, since, level, q, setParams]);

  const logs = useQuery({ queryKey: ['clone', c.id, 'logs', query], queryFn: () => endpoints.logs(c.id, query), enabled: c.status !== 'draft' });

  useEffect(() => {
    setLiveLines([]);
    if (!follow) return;
    setStreamState('idle');
    const { tail: _tail, since: _since, ...streamQ } = query;
    void _tail;
    void _since;
    return openStream(
      endpoints.logStreamPath(c.id, streamQ),
      { line: (d) => setLiveLines((prev) => (prev.length >= MAX_LINES ? [...prev.slice(-MAX_LINES + 1), d as LogLine] : [...prev, d as LogLine])) },
      { onOpen: () => setStreamState('open'), onError: () => setStreamState('error') },
    );
  }, [follow, query, c.id]);

  const lines = useMemo(() => [...(logs.data?.lines ?? []), ...liveLines], [logs.data, liveLines]);
  const toggle = (i: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });

  if (c.status === 'draft') return <EmptyState title="No logs yet" text="Logs appear once the clone is provisioned." />;

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }} useFlexGap>
          <TextField select label="Service" value={service} onChange={(e) => setService(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">Backend processes</MenuItem>
            {SERVICES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Since" value={since} onChange={(e) => setSince(e.target.value)} sx={{ minWidth: 90 }} disabled={follow}>
            {SINCE.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Level" value={level} onChange={(e) => setLevel(e.target.value)} sx={{ minWidth: 110 }}>
            <MenuItem value="">Any level</MenuItem>
            {LEVELS.map((s) => (
              <MenuItem key={s} value={s}>
                {s} and up
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          placeholder="Search text, request ID, ride ID…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          inputProps={{ 'aria-label': 'Search logs' }}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel control={<Switch checked={follow} onChange={(e) => setFollow(e.target.checked)} />} label="Follow live" />
          {follow && <Chip size="small" color={streamState === 'open' ? 'success' : streamState === 'error' ? 'error' : 'default'} label={streamState === 'open' ? 'live' : streamState === 'error' ? 'reconnecting' : 'connecting'} />}
          <Tooltip title="Reload">
            <IconButton onClick={() => void logs.refetch()} aria-label="Reload logs">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {logs.isError && <ErrorState error={logs.error} onRetry={() => void logs.refetch()} />}
      <Typography variant="caption" color="text.secondary">
        {logs.isFetching ? 'Loading…' : `${lines.length} lines`} · click a line to expand · JSON lines are pretty-printed
      </Typography>
      <VirtualLog
        items={lines}
        follow={follow}
        height="max(360px, calc(100vh - 340px))"
        render={(l, i) => <Row l={l} open={open.has(i)} onToggle={() => toggle(i)} />}
        empty={<Box sx={{ p: 2, color: 'text.secondary' }}>{logs.isLoading ? 'Loading…' : 'No lines match these filters.'}</Box>}
      />
    </Stack>
  );
}
